import { Component, computed, inject } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { CircularGraphComponent } from '@shared/components/circular-graph/circular-graph.component';
import { ICircularGraphData } from '@shared/components/circular-graph/circular-graph.interface';
import { DatatableComponent } from '@shared/components/datatable/datatable.component';
import { IDatatableConfiguration, TDatatableColumn } from '@shared/components/datatable/datatable.interface';
import { ISkeletonLoaderConfig } from '@shared/components/skeleton-loader/skeleton-loader.interface';
import { StackedBarsComponent } from '@shared/deliverables/stacked-bars/stacked-bars.component';
import { IStackedBarSegment } from '@shared/deliverables/stacked-bars/stacked-bars.interface';
import { NumberSeparatorUtilitiesService } from '@utilities/number-separator-utilities.service';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import {
  buildRankedCircularGraphData,
  buildRankedCircularGraphNameOverrides,
  EVENT_ANALYTICS_RANK_BAR_COLOR_CLASSES,
  EVENT_ANALYTICS_RANK_CIRCULAR_COLOR_MAP,
  EVENT_ANALYTICS_TOP_N,
  rankTopEntities,
} from '../admin-event-analytics-ranking.util';
import {
  IApplicationPerformanceKpis,
  IApplicationPerformanceKpisPerUser,
  IRankedEntity,
} from '../admin-event-analytics.interface';
import { AdminEventAnalyticsStore } from '../admin-event-analytics.store';
import { IUserBreakdownRow } from './admin-event-analytics-users.interface';

@Component({
  imports: [CircularGraphComponent, DatatableComponent, NgxSkeletonLoaderModule, StackedBarsComponent, TranslateModule],
  selector: 'app-admin-event-analytics-users',
  styleUrl: './admin-event-analytics-users.component.scss',
  templateUrl: './admin-event-analytics-users.component.html',
})
export class AdminEventAnalyticsUsersComponent {
  public readonly store = inject(AdminEventAnalyticsStore);

  public readonly rankColorMap = EVENT_ANALYTICS_RANK_CIRCULAR_COLOR_MAP;

  public readonly totalUsersCount = computed(() => (this.store.kpisPerUser$() ?? []).length);
  public readonly excludedUsersCount = computed(() => Math.max(0, this.totalUsersCount() - EVENT_ANALYTICS_TOP_N));

  public readonly mostActiveSegments = computed<IStackedBarSegment[]>(() =>
    this.toSegments(this.rankByMetric((kpis) => kpis.campaignCreationsFinished)),
  );

  public readonly campaignsCreatedRanking = computed<IRankedEntity[]>(() =>
    this.rankByMetric((kpis) => kpis.campaignCreationsFinished),
  );
  public readonly reportsCreatedRanking = computed<IRankedEntity[]>(() =>
    this.rankByMetric((kpis) => kpis.reportsCreated),
  );
  public readonly briefingsCreatedRanking = computed<IRankedEntity[]>(() =>
    this.rankByMetric((kpis) => kpis.briefingsCreated),
  );

  public readonly campaignsCreatedGraphData = computed<ICircularGraphData>(() =>
    buildRankedCircularGraphData(this.campaignsCreatedRanking()),
  );
  public readonly reportsCreatedGraphData = computed<ICircularGraphData>(() =>
    buildRankedCircularGraphData(this.reportsCreatedRanking()),
  );
  public readonly briefingsCreatedGraphData = computed<ICircularGraphData>(() =>
    buildRankedCircularGraphData(this.briefingsCreatedRanking()),
  );

  public readonly campaignsCreatedNameOverrides = computed(() =>
    buildRankedCircularGraphNameOverrides(this.campaignsCreatedRanking()),
  );
  public readonly reportsCreatedNameOverrides = computed(() =>
    buildRankedCircularGraphNameOverrides(this.reportsCreatedRanking()),
  );
  public readonly briefingsCreatedNameOverrides = computed(() =>
    buildRankedCircularGraphNameOverrides(this.briefingsCreatedRanking()),
  );

  // Sorted here (rather than relying solely on the datatable's own `defaultSort`) so the
  // underlying data source is always alphabetical ascending by name, regardless of the table's
  // interactive sort state (e.g. a persisted sort from a previous session).
  public readonly tableRows = computed<IUserBreakdownRow[]>(() =>
    (this.store.kpisPerUser$() ?? [])
      .map((entry) => this.buildRow(entry))
      .sort((a, b) => a.userName.localeCompare(b.userName)),
  );
  public readonly total = computed(() => this.tableRows().length);
  public readonly isTableDataLoaded = computed(() => this.store.kpisPerUser$() !== null);

  public readonly tableColumns: TDatatableColumn<IUserBreakdownRow, string>[] = [
    { id: 'userName', isClientSideSearchable: true, isSortable: true, name: 'field.userName' },
    { id: 'userGroupName', isClientSideSearchable: true, isSortable: true, name: 'field.group' },
    { id: 'campaignsStarted', isSortable: true },
    { id: 'campaignsCompleted', isSortable: true },
    { dataViewStrategy: (row) => row.completionPercentageSortValue, id: 'completionPercentage', isSortable: true },
    { id: 'reportsCreated', isSortable: true },
    { id: 'briefingsCreated', isSortable: true },
  ];

  public readonly tableConfiguration: IDatatableConfiguration<IUserBreakdownRow> = {
    defaultSort: { active: 'userName', direction: 'asc' },
    isPaginationEnabled: true,
    isRowClickEnabled: false,
    isSearchEnabled: true,
    pageSizeOptions: [10, 25, 50, 100],
  };

  // Column widths mirror `admin-event-analytics-users.component.scss`'s `.mat-column-*` rules, so
  // the skeleton lines up exactly with the real table once it loads.
  public readonly tableLoaderConfig: ISkeletonLoaderConfig = {
    columns: [
      {
        borderRadius: '4px',
        className: 'w-[16%]',
        columnPadding: '0 8px 0 16px',
        rowPadding: '18px 0',
        textWidth: '100px',
        type: 'text',
      },
      { borderRadius: '4px', className: 'w-[16%]', textWidth: '110px', type: 'text' },
      { borderRadius: '4px', className: 'w-[14%]', textWidth: '50px', type: 'text' },
      { borderRadius: '4px', className: 'w-[14%]', textWidth: '50px', type: 'text' },
      { borderRadius: '4px', className: 'w-[14%]', textWidth: '60px', type: 'text' },
      { borderRadius: '4px', className: 'w-[13%]', textWidth: '50px', type: 'text' },
      { borderRadius: '4px', className: 'w-[13%]', textWidth: '50px', type: 'text' },
    ],
    rows: 5,
    showHeaderControls: true,
    showPaginator: true,
    showSearchField: true,
  };

  private readonly numberSeparatorUtilities = inject(NumberSeparatorUtilitiesService);

  // Data is fetched by the parent `AdminEventAnalyticsComponent` (see its constructor) - owning
  // the fetch there instead of here means reopening this tab (which Angular Material's
  // `mat-tab-group` re-instantiates from scratch each time via `matTabContent`) never re-triggers
  // a redundant request or its loading flicker for data that's already loaded and hasn't gone stale.

  private rankByMetric(getValue: (kpis: IApplicationPerformanceKpis) => number): IRankedEntity[] {
    return rankTopEntities(
      (this.store.kpisPerUser$() ?? []).map((entry) => ({
        id: entry.userId,
        name: entry.userName,
        value: getValue(entry.kpis),
      })),
    );
  }

  private toSegments(ranking: IRankedEntity[]): IStackedBarSegment[] {
    return ranking.map((entity, index) => ({
      colorClass: EVENT_ANALYTICS_RANK_BAR_COLOR_CLASSES[index],
      id: entity.id,
      label: entity.name,
      value: entity.value,
    }));
  }

  private buildRow(entry: IApplicationPerformanceKpisPerUser): IUserBreakdownRow {
    const { kpis } = entry;

    return {
      briefingsCreated: kpis.briefingsCreated,
      campaignsCompleted: kpis.campaignCreationsFinished,
      campaignsStarted: kpis.campaignCreationsStarted,
      completionPercentage: this.formatPercentage(kpis.campaignCompletionRate),
      completionPercentageSortValue: kpis.campaignCompletionRate,
      id: entry.userId,
      reportsCreated: kpis.reportsCreated,
      userGroupName: entry.userGroupName,
      userName: entry.userName,
    };
  }

  private formatPercentage(value: number): string {
    // Guards against a non-finite backend value (e.g. NaN from a 0/0 division) rendering as "NaN%".
    const safeValue = Number.isFinite(value) ? value : 0;

    return `${this.numberSeparatorUtilities.formatNumberToLocaleString(safeValue * 100, null, 1, 1, true)}%`;
  }
}
